import Homepage from '@codex-applications/homepage';
import { get, NO_REDIRECT_WHEN_UNAUTHORIZED } from '@codex/helpers';
import { paths, translations } from '@dc/ui-library';
import QuickAccount from '@dc/ui-library/src/apps/QuickAccount';
import {
  VIEW_HOMEPAGE_MODULE_QUEST_REGISTRATION,
  VIEW_HOMEPAGE_MODULE_QUEST_SIGN_IN
} from '@dc/ui-library/src/apps/QuickAccount/constants';
import Portal from '@dc/ui-library/src/gadgets/Portal';
import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { RECENT_ORDERS_API_ENDPOINT, WISHLIST_API_ENDPOINT } from './contants';
import { getTitle, mapOrders, mapOrdersForAnalytics, mapWishlistForAnalytics } from './helpers';

const Wrapper = styled.div`
  margin: ${({ theme }) => theme.spacing[4]} 0;
  max-width: 1240px;
  box-sizing: border-box;
  
  * {
    box-sizing: border-box;
  }
`;

class HomepageModuleWrapper extends PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      activeOrderId: undefined,
      orders: [],
      ordersTitle: undefined,
      quickAccountPopupOpened: false,
      userHasAccount: false,
      wishlist: [],
      wishlistTitle: undefined
    };

    this.openQuickAccount = this.openQuickAccount.bind(this);
    this.closeQuickAccount = this.closeQuickAccount.bind(this);
  }

  componentDidMount() {
    this.fetchData();
  }

  async fetchData() {
    // Load in parallel
    const ordersLoad = this.fetchOrders();
    const wishlistLoad = this.fetchWishlist();

    // But wait until both is loaded
    await ordersLoad;
    await wishlistLoad;

    this.dispatchAnalytics();
  }

  async fetchOrders() {
    const { props } = this;

    const ordersData = await get(
      props.customerId
        ? `${RECENT_ORDERS_API_ENDPOINT}?customerId=${props.customerId}`
        : RECENT_ORDERS_API_ENDPOINT,
      undefined,
      undefined,
      NO_REDIRECT_WHEN_UNAUTHORIZED
    );

    if (!ordersData || !ordersData.orders) {
      return;
    }

    const orders = ordersData.orders.slice(0, 2).map(mapOrders);

    const userFirstName = ordersData.userFullName ? ordersData.userFullName.split(' ')[0] : '';

    this.setState({
      orders: orders,
      ordersTitle: getTitle(
        userFirstName
          ? translations.recent_orders_title
          : translations.recent_orders_title_anonymous,
        userFirstName,
        ordersData.unfilteredCount || 0,
        props.isGuest ? undefined : paths.MY_ACCOUNT_PATH,
        'Orders Module: view all'
      ),
      userHasAccount: ordersData.userHasAccount
    });
  }

  async fetchWishlist() {
    if (this.props.isGuest) {
      return;
    }

    const wishlistData = await get(
      WISHLIST_API_ENDPOINT,
      undefined,
      undefined,
      NO_REDIRECT_WHEN_UNAUTHORIZED
    );

    if (!wishlistData || !wishlistData.products) {
      return;
    }

    const wishlist = wishlistData.products.slice(0, 2);

    this.setState({
      wishlist: wishlist,
      wishlistTitle: getTitle(
        translations.recently_saved_items_title,
        undefined,
        wishlistData.countOfProducts || 0,
        `${paths.MY_ACCOUNT_PATH}#/:wishlist`,
        'Saved items module: view all'
      )
    });
  }

  dispatchAnalytics() {
    const { props, state } = this;

    props.dispatch({
      type: 'HOMEPAGE_MODULE_LOADED',
      payload: {
        data: {
          orders: state.orders.map(mapOrdersForAnalytics),
          savedItemsModule: state.wishlist.length > 1,
          wishlistItems: state.wishlist.map(mapWishlistForAnalytics)
        },
        trackFunctionArgs: 'Module loaded'
      }
    });
  }

  openQuickAccount(e, order) {
    e.preventDefault();

    this.setState({
      quickAccountPopupOpened: true,
      activeOrderId: order.singleOrders[0].orderId
    });
  }

  closeQuickAccount() {
    this.setState({
      quickAccountPopupOpened: false
    });
  }

  render() {
    const { props, state } = this;

    if (state.orders.length <= 0) {
      return false;
    }

    return (
      <Wrapper>
        <>
          <Homepage
            guest={props.isGuest}
            noRatingsLabel={translations.no_reviews_label}
            orders={state.orders}
            ordersTitle={state.ordersTitle}
            wishlist={state.wishlist}
            wishlistTitle={state.wishlistTitle}
            onRegisterAccountLinkClicked={this.openQuickAccount}
            registerAccountMessageStart={state.userHasAccount
              ? translations.add_order_to_account_link_text
              : translations.register_account_link_text}
            registerAccountMessageEnd={state.userHasAccount
              ? translations.add_order_to_account_suffix_text
              : translations.register_account_suffix_text}
          />
          {state.quickAccountPopupOpened && (
            <Portal domNode={document.body}>
              <QuickAccount
                customerId={props.customerId}
                customerEmail={props.customerEmail}
                initialView={state.userHasAccount
                  ? VIEW_HOMEPAGE_MODULE_QUEST_SIGN_IN
                  : VIEW_HOMEPAGE_MODULE_QUEST_REGISTRATION}
                orderId={state.activeOrderId}
                onRegistrationSuccess={this.closeQuickAccount}
                onPopupClose={this.closeQuickAccount}
              />
            </Portal>
          )}
        </>
      </Wrapper>
    );
  }

}

HomepageModuleWrapper.displayName = 'HomepageModuleWrapper';

HomepageModuleWrapper.propTypes = {
  dispatch: PropTypes.func.isRequired,
  customerId: PropTypes.number,
  customerEmail: PropTypes.string,
  isGuest: PropTypes.bool
};

HomepageModuleWrapper.defaultProps = {
  customerId: undefined,
  customerEmail: undefined,
  isGuest: false
};

export default HomepageModuleWrapper;
